import _ from "lodash";
/** external */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, Modal, SelectDropdown } from "@toddle-edu/ds-web";
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import update from "immutability-helper";
/** internal */
import { getDifficultyLevelOptions } from "QuestionBank/modules/Utils";
import {
  updateQuestionLibraryTags,
  createQuestionTopic,
  addQuestionToSchoolLibrary,
  updateLoadingState,
  addQuestionToSchoolLibraryV2,
} from "Worksheet/modules/WorksheetModules";
import {
  getOrganizationSubjectsEnhancer,
  getOrganizationGradeBasicDetailsEnhancer,
  getQuestionTopicsEnhancer,
} from "Worksheet/modules/WorksheetEnhancers";
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import { QUESTION_TOPIC } from "QuestionBank/modules/Constants";
import Loading from "UIComponents/Loading";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
/** relative */
import classes from "./AddToLibraryModal.scss";
import FieldErrorMessage from "./FieldErrorMessage";

const styles = {
  filtersContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
};

const AddToLibraryModal = props => {
  const {
    toggleAddToLibraryModal,
    t,
    gradeOptions,
    subjectOptions,
    topics,
    updateQuestionLibraryTags,
    createQuestionTopic,
    tagDetails,
    isLoading,
    addQuestionToSchoolLibrary,
    questionId,
    updateLoadingState,
    setToastMsg,
    curriculumType,
    isQueriesV2Enabled,
    addQuestionToSchoolLibraryV2,
  } = props;

  const [showError, setShowError] = useState(false);
  const [errorState, setErrorState] = useState({
    subjects: false,
    grades: false,
  });
  const [initialTagDetails, setInitialTagDetails] = useState({
    topics: [],
    subjects: [],
    grades: [],
    difficulty: [],
  });
  const [disableAddButton, setDisableAddButton] = useState(false);

  useEffect(() => {
    setInitialTagDetails(tagDetails);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const difficultyOptions = getDifficultyLevelOptions(t);
  const topicOptions = _.map(topics, topic => ({
    value: _.get(topic, "id"),
    label: _.get(topic, "label"),
  }));

  const checkForError = selectedTags => {
    const updatedErrorState = {
      subjects: _.isEmpty(_.get(selectedTags, "subjects", [])),
      grades: _.isEmpty(_.get(selectedTags, "grades", [])),
    };

    setErrorState(updatedErrorState);
    return Object.values(updatedErrorState).some(value => value === true);
  };

  const updateTag = (key, value) => {
    const updatedTagDetails = update(tagDetails, {
      [key]: { $set: value },
    });
    const updatedErrorState = update(errorState, {
      [key]: { $set: _.isEmpty(value) },
    });
    updateQuestionLibraryTags(updatedTagDetails);
    setErrorState(updatedErrorState);
  };

  const createNewQuestionTopic = async value => {
    setDisableAddButton(true);
    await createQuestionTopic({ label: value });
    setDisableAddButton(false);

    const updatedErrorState = update(errorState, {
      topics: { $set: false },
    });

    setErrorState(updatedErrorState);
  };

  const updateTopics = value => updateTag("topics", value);
  const updateSubject = value => updateTag("subjects", value);
  const updateGrade = value => updateTag("grades", value);
  const updateDifficulty = value => updateTag("difficulty", value);

  const onAddButtonClick = async () => {
    setShowError(true);
    if (!checkForError(tagDetails)) {
      updateLoadingState(true);
      toggleAddToLibraryModal();
      if (isQueriesV2Enabled) {
        await addQuestionToSchoolLibraryV2({ id: questionId });
      } else {
        //WORKSHEET_RESTRUCTURING:Remove this once all questions are migrated to queries v2
        await addQuestionToSchoolLibrary({ id: questionId });
      }

      // reset topic and difficulty tags
      updateQuestionLibraryTags({
        topics: [],
        difficulty: [],
      });

      updateLoadingState(false);
    } else {
      setToastMsg({
        msg: "toastMsgs:please_fill_all_required_fields",
        type: "warning",
      });
    }
  };

  const onDiscardClick = () => {
    updateQuestionLibraryTags(initialTagDetails);
    toggleAddToLibraryModal();
  };

  const showSubjectError = showError && _.get(errorState, "subjects");
  const showGradeError = showError && _.get(errorState, "grades");

  /**
   * @returns subject options for diff curriculum type
   * for "IB_MYP", "IB_DP" it will be grouped by subject group
   * if total valid group ids is not equal to total subjects, it will be simply alpha order by label
   */
  const _subjectOptions = React.useMemo(() => {
    let _subjectOptions = subjectOptions;
    const subjectGroupIDs = _.compact(_.map(subjectOptions, "subjectGroup.id"));
    if (
      _.includes(["IB_MYP", "IB_DP"], curriculumType) &&
      _.size(subjectOptions) === _.size(subjectGroupIDs)
    ) {
      // * MYP and DP curriculum, showing subject group wise
      const groupedSubject = [];
      _.forEach(subjectOptions, subject => {
        if (_.get(subject, "subjectGroup")) {
          const subjectGroupId = _.get(subject, "subjectGroup.id");
          const groupedItem = _.find(groupedSubject, { value: subjectGroupId });
          const subjectObject = _.pick(subject, ["value", "label"]);
          if (groupedItem) {
            groupedItem.options.push(subjectObject);
          } else {
            groupedSubject.push({
              value: subjectGroupId,
              options: [subjectObject],
              label: _.get(subject, "subjectGroup.name"),
            });
          }
        }
      });
      _subjectOptions = groupedSubject;
    }
    return _.orderBy(_subjectOptions, "label");
  }, [curriculumType, subjectOptions]);

  return (
    <Modal
      hasOverlay={true}
      height={600}
      width={480}
      onClose={onDiscardClick}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      testId="worksheet-addToLibraryModal-closeModal"
    >
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Modal.Header
            isClosable={true}
            title={t("common:add_to_label", {
              label: t("common:school_library"),
            })}
          ></Modal.Header>
          <Modal.Body isScrollable={true} style={styles.filtersContainer}>
            {/* Grades */}
            <div className={classes.fieldContainer}>
              <div className="text-label-2">
                {t("common:mandatory_label", {
                  label: t("worksheet:applicable_grade_one_or_more"),
                })}
              </div>
              <div className={classes.dropdownContainer}>
                <SelectDropdown
                  isClearable={true}
                  isCreatable={false}
                  isMulti={true}
                  hasMultilineOptions={true}
                  size={"large"}
                  placeholder={t("common:select_with_label", {
                    label: t("common:grade_plural"),
                  })}
                  options={gradeOptions}
                  onChange={updateGrade}
                  value={_.get(tagDetails, "grades", [])}
                  error={showGradeError}
                  testId="worksheet-addToLibraryModal-grades"
                />
                {showGradeError ? <FieldErrorMessage t={t} /> : null}
              </div>
            </div>

            {/* Subjects */}
            <div className={classes.fieldContainer}>
              <div className="text-label-2">
                {t("common:mandatory_label", {
                  label: t("common:subject_one_or_more"),
                })}
              </div>
              <div className={classes.dropdownContainer}>
                <SelectDropdown
                  isClearable={true}
                  isCreatable={false}
                  isMulti={true}
                  hasMultilineOptions={true}
                  size={"large"}
                  placeholder={t("common:select_with_label", {
                    label: t("common:subject_plural"),
                  })}
                  options={_subjectOptions}
                  onChange={updateSubject}
                  value={_.get(tagDetails, "subjects", [])}
                  error={showSubjectError}
                  testId="worksheet-addToLibraryModal-subjects"
                />
                {showSubjectError ? <FieldErrorMessage t={t} /> : null}
              </div>
            </div>

            {/* Topics */}
            <div className={classes.fieldContainer}>
              <div className="text-label-2">{t("worksheet:topics")}</div>
              <div className={classes.dropdownContainer}>
                <SelectDropdown
                  isClearable={true}
                  isCreatable={true}
                  isMulti={true}
                  hasMultilineOptions={true}
                  size={"large"}
                  placeholder={t("worksheet:type_to_add_or_create_topics")}
                  options={topicOptions}
                  onChange={updateTopics}
                  value={_.get(tagDetails, "topics", [])}
                  onCreateOption={createNewQuestionTopic}
                  testId="worksheet-addToLibraryModal-topics"
                />
              </div>
            </div>

            {/* Difficulty level */}
            <div className={classes.fieldContainer}>
              <div className="text-label-2">
                {t("worksheet:difficulty_level")}
              </div>
              <div className={classes.dropdownContainer}>
                <SelectDropdown
                  isClearable={true}
                  isCreatable={false}
                  hasMultilineOptions={true}
                  size={"large"}
                  placeholder={t("common:select_with_label", {
                    label: t("worksheet:difficulty_level"),
                  })}
                  options={difficultyOptions}
                  onChange={updateDifficulty}
                  value={_.get(tagDetails, "difficulty", [])}
                  testId="worksheet-addToLibraryModal-difficultyLevel"
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.modalFooterContainer}>
              <Button
                dsVersion="2.0"
                variant="progressive"
                size="large"
                _legacy_variant="progressive"
                _legacy_size="large"
                onClick={onAddButtonClick}
                disabled={disableAddButton}
                testId="worksheet-addToLibraryModal-modalFooter-addQuestionToLibrary"
              >
                {t("common:add")}
              </Button>
              <Button
                onClick={onDiscardClick}
                dsVersion="2.0"
                type="outlined"
                variant="neutral"
                size="large"
                _legacy_variant="outlined-subtle"
                _legacy_size="large"
                testId="worksheet-addToLibraryModal-modalFooter-discard"
              >
                {t("common:discard")}
              </Button>
            </div>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};
const mapStateToProps = state => {
  const tagDetails = _.get(state, "worksheet.questionLibraryTags", {});
  const organizationId = _.get(state, "login.userInfo.org_id");
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumType = getCurrentCurriculumProgramType(state);

  const questionTopicVariables = {
    input: {
      types: [QUESTION_TOPIC],
    },
  };

  const getOrganizationGradesVariables = {
    organizationId,
    filters: {
      curriculumProgramIds: [curriculumProgramId],
    },
  };

  const getOrganizationSubjectsVariables = {
    organizationId,
    filters: {
      curriculumProgramIds: [curriculumProgramId],
    },
  };

  return {
    tagDetails,
    questionTopicVariables,
    organizationId,
    curriculumType,
    getOrganizationGradesVariables,
    getOrganizationSubjectsVariables,
  };
};

const mapActionCreators = {
  updateQuestionLibraryTags,
  createQuestionTopic,
  addQuestionToSchoolLibrary,
  updateLoadingState,
  setToastMsg,
  addQuestionToSchoolLibraryV2,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  getOrganizationSubjectsEnhancer(),
  getOrganizationGradeBasicDetailsEnhancer(),
  getQuestionTopicsEnhancer()
)(AddToLibraryModal);

AddToLibraryModal.propTypes = {
  toggleAddToLibraryModal: PropTypes.func,
  t: PropTypes.func,
  gradeOptions: PropTypes.array,
  subjectOptions: PropTypes.array,
  topics: PropTypes.array,
  updateQuestionLibraryTags: PropTypes.func,
  tagDetails: PropTypes.object,
  isLoading: PropTypes.bool,
  questionId: PropTypes.string,
  isQueriesV2Enabled: PropTypes.bool,
};

AddToLibraryModal.defaultProps = {
  gradeOptions: [],
  subjectOptions: [],
  topics: [],
  tagDetails: {},
  isLoading: false,
  isQueriesV2Enabled: false,
};
